import { useDialog } from '@dni-hooks/useDialog'
import { type Recommendation } from '@hooks-api/types/dataModel/butler'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { HStack } from '@pets-ui/HStack/HStack'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useButlerListItemSearch, type UseListItemSearchArgs } from '../../hooks/useButlerListItemSearch'
import { ButlerRecommendedListItem } from './ButlerRecommendedListItem'
import { getProductName } from './getProductName'
import { ProductSearchInput } from './ProductSearchInput'

export const DIALOG_CHANGE_BUTLER_ITEM = 'DialogChangeButlerItem'

const styles = stylex.create({
  confirmButton: {
    backgroundColor: colors.black,
  },
})

export interface DialogChangeButlerItemProps {
  recommendations?: Recommendation[]
  variationId?: number
  onExchangeProduct?: UseListItemSearchArgs['onExchangeProduct']
}

export const DialogChangeButlerItem = () => {
  const { recommendations, variationId, onExchangeProduct, open } = useDialog<DialogChangeButlerItemProps>(DIALOG_CHANGE_BUTLER_ITEM)
  const {
    selectedExchangeProduct,
    isExchanging,
    currentProduct,
    handleClose,
    handleSelectProduct,
    handleExchangeProduct,
    handleInputChange,
    handleClearInputValue,
    handleSearchSubmit,
    inputValue,
    searchProducts,
    isLoading,
  } = useButlerListItemSearch({ recommendations, variationId, onExchangeProduct })
  const renderContent = () => {
    if (isLoading) {
      return <Box display="flex" justifyContent="center" alignItems="center" height="200px"><CircularProgress fontSize={24} color={colors.grey600} /></Box>
    }
    if (!searchProducts) {
      return (
        <>
          <VStack gap={5} mt={2}>
            {recommendations?.map((recommendation, index) => {
              return (
                <ButlerRecommendedListItem
                  key={recommendation.wp_variation_id}
                  recommendedIndex={index + 1}
                  title={recommendation.title}
                  subTitle={recommendation.shipping_name}
                  productImgSrc={recommendation.thumbnail}
                  selected={
                    selectedExchangeProduct
                      ? recommendation.wp_variation_id === selectedExchangeProduct?.variation_id
                      : recommendation.wp_variation_id === currentProduct?.wp_variation_id
                  }
                  onClick={handleSelectProduct({
                    parent_product_id: recommendation.wp_product_id,
                    variation_id: recommendation.wp_variation_id,
                    sku: recommendation.sku,
                    quantity: recommendation.quantity,
                    stock: recommendation.stock,
                  })}
                />
              )
            })}
          </VStack>
        </>
      )
    }
    if (searchProducts.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px"><Text>找不到商品</Text></Box>
      )
    }
    return (
      <VStack gap={3} mt={2}>
        {searchProducts.map(product => (
          <ButlerRecommendedListItem
            key={product.variation_id}
            selected={product.variation_id === selectedExchangeProduct?.variation_id}
            title={getProductName(product.title, product.attributes)}
            subTitle={`${product.name} (${product.sku})`}
            onClick={handleSelectProduct({
              parent_product_id: product.parent_product_id ?? product.variation_id,
              variation_id: product.variation_id,
              sku: product.sku,
              stock: product.stock,
            })}
          />
        ))}
      </VStack>
    )
  }

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      fullWidth
    >
      <DialogHeader title="更換商品" onClose={handleClose} />
      <HStack p={4} justifyContent="space-between" alignItems="flex-end">
        <Text variant="caption" color={colors.grey600}>選項由 AI 推薦信心度排序</Text>
        <ProductSearchInput
          onChange={handleInputChange}
          onClearInputValue={handleClearInputValue}
          onSubmit={handleSearchSubmit}
          value={inputValue}
          placeholder="找商品"
        />
      </HStack>
      <DialogContent pt={0}>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="grey" onClick={handleClose} width={80} disabled={isExchanging}>取消</Button>
        <Button variant="filled" rootStyle={styles.confirmButton} width={80} disabled={!selectedExchangeProduct} loading={isExchanging} onClick={handleExchangeProduct}>確定</Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}
